import config from "../config";
import http from "../api/apiClient";

const stripTrailingSlashes = (value) => String(value || "").replace(/\/+$/, "");

const buildIssuesEventsUrl = () => {
  const apiBase = stripTrailingSlashes(config?.apiBaseUrl);
  return `${apiBase}/issues/events`;
};

const parseJsonLoose = (value) => {
  if (value == null) return null;
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
};

const extractIssueMeta = (payload) => {
  const issue =
    payload?.issue ||
    payload?.data?.issue ||
    payload?.entity?.issue ||
    payload?.issueEvent?.issue ||
    null;
  const number = issue?.number ?? issue?.id ?? payload?.issueNumber ?? payload?.issue_id ?? payload?.id ?? null;
  const title = issue?.title ?? payload?.title ?? payload?.issueTitle ?? null;
  return { issue, number, title };
};

const describeWorkflowEvent = (payload) => {
  const { number, title } = extractIssueMeta(payload);
  const next =
    payload?.workflowStatus ??
    payload?.workflow_status ??
    payload?.status ??
    payload?.to ??
    payload?.next ??
    payload?.new ??
    null;
  const prev =
    payload?.from ??
    payload?.previous ??
    payload?.old ??
    payload?.prev ??
    null;

  const idPart = number != null ? `#${number}` : "Issue";
  const titlePart = title ? ` — ${title}` : "";

  const message =
    next && prev
      ? `${idPart}${titlePart}\n${String(prev)} → ${String(next)}`
      : next
        ? `${idPart}${titlePart}\nNieuwe status: ${String(next)}`
        : `${idPart}${titlePart}`;

  const href = number != null ? `/profile?issue=${encodeURIComponent(number)}` : null;
  return {
    title: "Workflowstatus gewijzigd",
    message,
    level: "info",
    sticky: true,
    href,
    actionLabel: number != null ? `Open #${number}` : "Open",
  };
};

const describeCommentEvent = (payload) => {
  const { number, title } = extractIssueMeta(payload);
  const comment =
    payload?.comment ||
    payload?.data?.comment ||
    payload?.entity?.comment ||
    null;
  const author =
    comment?.author?.name ||
    comment?.author?.username ||
    comment?.user?.name ||
    comment?.user?.username ||
    payload?.author ||
    payload?.user ||
    null;
  const body = comment?.body ?? comment?.text ?? payload?.body ?? payload?.text ?? null;

  const idPart = number != null ? `#${number}` : "Issue";
  const titlePart = title ? ` — ${title}` : "";
  const bodyPart = body ? `\n${String(body).slice(0, 220)}` : "";
  const authorPart = author ? ` (${author})` : "";

  const href = number != null ? `/profile?issue=${encodeURIComponent(number)}` : null;
  return {
    title: `Nieuwe comment${authorPart}`,
    message: `${idPart}${titlePart}${bodyPart}`,
    level: "info",
    sticky: true,
    href,
    actionLabel: number != null ? `Open #${number}` : "Open",
  };
};

const describeApplicationNewEvent = (payload) => {
  const application =
    payload?.application ||
    payload?.data?.application ||
    payload?.entity?.application ||
    payload?.familyApplication ||
    null;
  const id =
    application?.id ??
    application?.applicationId ??
    payload?.applicationId ??
    payload?.id ??
    null;
  const childName =
    application?.childName ||
    application?.child_name ||
    payload?.childName ||
    payload?.child_name ||
    null;
  const parentName =
    application?.parentName ||
    application?.parent_name ||
    payload?.parentName ||
    payload?.parent_name ||
    null;

  const who = [childName, parentName].filter(Boolean).join(" — ");
  const message = who ? who : id != null ? `Aanmelding #${id}` : "Nieuwe aanmelding ontvangen.";

  return {
    title: "Nieuwe aanmelding",
    message,
    level: "info",
    sticky: true,
    href: "/school/applications",
    actionLabel: "Open aanmeldingen",
  };
};

const describeMutationNewEvent = (payload) => {
  const mutation =
    payload?.mutation ||
    payload?.request ||
    payload?.mutationRequest ||
    payload?.data?.mutation ||
    payload?.data?.request ||
    payload?.data?.mutationRequest ||
    null;
  const id = mutation?.id ?? mutation?.requestId ?? payload?.requestId ?? payload?.id ?? null;
  const kind = mutation?.kind || mutation?.type || payload?.kind || payload?.type || null;
  const subject = mutation?.subject || mutation?.childName || mutation?.child_name || payload?.subject || payload?.childName || null;

  const parts = [kind, subject].filter(Boolean);
  const message =
    parts.length > 0 ? parts.join(" — ") : id != null ? `Mutatieverzoek #${id}` : "Nieuw mutatieverzoek ontvangen.";

  return {
    title: "Nieuw mutatieverzoek",
    message,
    level: "info",
    sticky: true,
    href: "/school/mutations",
    actionLabel: "Open mutaties",
  };
};

const describeDuoRegistrationReadyEvent = (payload) => ({
  title: "DUO-aanmelding gereed",
  message: payload?.childName || "Een leerling kan bij DUO worden aangemeld.",
  level: "info",
  sticky: true,
  href: payload?.href || "/reports/duo-registrations",
  actionLabel: "Open DUO-aanmeldingen",
});

const getSseTicket = async () => {
  try {
    const res = await http.get("/issues/events/ticket");
    const ticket =
      res?.data?.ticket ||
      res?.data?.nonce ||
      res?.data?.token ||
      (typeof res?.data === "string" ? res.data : null);
    return ticket ? String(ticket) : null;
  } catch {
    return null;
  }
};

export const startIssuesEventSource = ({
  onToast,
  onStatus,
  withCredentials = true,
  preferTicket = true,
  lastEventId = null,
  onLastEventId,
} = {}) => {
  let closed = false;
  let es = null;

  const open = async () => {
    const base = buildIssuesEventsUrl();
    let url = base;

    if (preferTicket) {
      const ticket = await getSseTicket();
      if (ticket) {
        const separator = url.includes("?") ? "&" : "?";
        url = `${url}${separator}ticket=${encodeURIComponent(ticket)}`;
      }
    }

    if (lastEventId) {
      const separator = url.includes("?") ? "&" : "?";
      url = `${url}${separator}lastEventId=${encodeURIComponent(String(lastEventId))}`;
    }

    const init = { withCredentials: Boolean(withCredentials) };
    es = new EventSource(url, init);

    if (typeof onStatus === "function") onStatus({ state: "connecting", url });

    const handle = (type, raw) => {
      const payload = parseJsonLoose(raw?.data);
      const nextLastEventId = raw?.lastEventId || raw?.id || payload?.id || payload?.eventId || payload?.event_id || null;
      if (nextLastEventId && typeof onLastEventId === "function") {
        try {
          onLastEventId(String(nextLastEventId));
        } catch {
          // ignore
        }
      }
      if (typeof onStatus === "function") onStatus({ state: "event", type });
      if (typeof onToast !== "function") return;

      if (type === "issue.workflow") onToast({ ...describeWorkflowEvent(payload), kind: type, payload });
      else if (type === "issue.comment") onToast({ ...describeCommentEvent(payload), kind: type, payload });
      else if (type === "application.new") onToast({ ...describeApplicationNewEvent(payload), kind: type, payload });
      else if (type === "mutation.new") onToast({ ...describeMutationNewEvent(payload), kind: type, payload });
      else if (type === "child.duo-registration.ready") {
        window.dispatchEvent(new CustomEvent("lumen:duo-registration-changed"));
        onToast({ ...describeDuoRegistrationReadyEvent(payload), kind: type, payload });
      }
      else onToast({ title: type, message: typeof payload === "string" ? payload : JSON.stringify(payload), level: "info" });
    };

    es.addEventListener("open", () => {
      if (typeof onStatus === "function") onStatus({ state: "open", url });
    });

    es.addEventListener("error", (err) => {
      if (typeof onStatus === "function") onStatus({ state: "error", url, error: err });
    });

    es.addEventListener("issue.workflow", (e) => handle("issue.workflow", e));
    es.addEventListener("issue.comment", (e) => handle("issue.comment", e));
    es.addEventListener("application.new", (e) => handle("application.new", e));
    es.addEventListener("mutation.new", (e) => handle("mutation.new", e));
    es.addEventListener("child.duo-registration.ready", (e) => handle("child.duo-registration.ready", e));

    es.addEventListener("message", (e) => {
      const payload = parseJsonLoose(e?.data);
      const type = payload?.type || payload?.event || payload?.kind || "message";
      handle(String(type), { data: e?.data, lastEventId: e?.lastEventId });
    });
  };

  open();

  return {
    close: () => {
      closed = true;
      try {
        es?.close?.();
      } catch {
        // ignore
      }
    },
    get closed() {
      return closed;
    },
  };
};
